import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const AssetGroupContext = createContext();

export const AssetGroupProvider = ({ children }) => {
  const [assetGroups, setAssetGroups] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchAssetGroups = useCallback(async () => {
    setAssetGroups((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/asset-groups${queryString}`);
      if (response.success) {
        setAssetGroups({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setAssetGroups({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchAssetGroups();
  }, [fetchAssetGroups]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = assetGroups.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <AssetGroupContext.Provider
      value={{
        assetGroups,
        filterOptions,
        handleSearch,
        refetch: fetchAssetGroups,
        Pagination,
      }}
    >
      {children}
    </AssetGroupContext.Provider>
  );
};

export const useAssetGroup = () => {
  const context = useContext(AssetGroupContext);
  if (!context) {
    throw new Error("useAssetGroup must be used within AssetGroupProvider");
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/ahr/asset-groups?search=${inputValue}`);
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};
